import {IDropdownMenuItem} from '@atomaro/ui-kit/components/DropdownMenu/types';
import {useApiariesStore} from 'entities/apiaries';
import {
  CalendarPatchRequestParams,
  CalendarPostRequestParams,
  useCalendarStore,
} from 'entities/calendar';
import {useHivesStore} from 'entities/hives';
import {ChangeEvent, useEffect, useRef, useState} from 'react';
import {ControllerRenderProps, useForm} from 'react-hook-form';
import {toast} from 'react-toastify';
import {notifySelectItems} from 'shared/consts';
import {toRem} from 'shared/helpers';
import {useDebounceCallback} from 'shared/lib/debounce/useDebounce';
import {NewEventFormValues} from 'shared/types';
import {getEndAt, getStartAt, pickerDateToUtcIso} from '../helpers/dates';
import {useUserStore} from 'entities/user';

interface FullCalendarPatchRequestParams extends CalendarPatchRequestParams {
  hiveIds: string[];
}

const superDefaultValues: NewEventFormValues = {
  hiveIds: [],
  hives: [],
  description: '',
  startAt: getStartAt(),
  startAtUtc: '',
  endAt: getEndAt(),
  endAtUtc: '',
  notifyAt: undefined,
  notifyAtNum: 'null',
  notifyAtRepeat: undefined,
  notifyAtRepeatNum: 'null',
};

type useCalendarNewEventProps = {
  defaultValues?: Partial<NewEventFormValues>;
  editId?: string | number;
  onClose?: () => void;
};

export const useCalendarNewEvent = ({defaultValues, editId, onClose}: useCalendarNewEventProps) => {
  const hives = useHivesStore((s) => s.hives);
  const addEvent = useCalendarStore((s) => s.addEvent);
  const editEvent = useCalendarStore((s) => s.editEvent);
  const getEventsFromServer = useCalendarStore((s) => s.getEventsFromServer);
  const apiaryId = useApiariesStore((state) => state.currentApiaryId);
  const user = useUserStore((s) => s.userInfo);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [noteHeight, setNoteHeight] = useState<string>(toRem(76));
  const [isEndReadOnly, setIsEndReadOnly] = useState<boolean>(false);
  const [endAtMinDate, setEndAtMinDate] = useState<number>(getEndAt().getTime());

  const [notifyAtDefaultValue, setNotifyAtDefaultValue] = useState<string | number>('null');
  const [notifyAtRepeatDefaultValue, setNotifyAtRepeatDefaultValue] = useState<string | number>(
    'null'
  );

  const tempEndAt = useRef<Date | null>(null);
  const notifyAtValue = useRef<string | number>('null');
  const notifyAtRepeatValue = useRef<string | number>('null');

  const {
    control,
    formState: {errors},
    setValue,
    handleSubmit,
    reset,
    getValues,
  } = useForm<NewEventFormValues>({
    defaultValues: {
      ...superDefaultValues,
      ...defaultValues,
    },
  });

  useEffect(() => {
    if (!editId || !defaultValues) return;

    const notifyNum =
      defaultValues.notifyAt && defaultValues.startAt
        ? defaultValues.notifyAt.getTime() - defaultValues.startAt.getTime()
        : 'null';
    const notifyRepeatNum =
      defaultValues.notifyAtRepeat && defaultValues.startAt
        ? defaultValues.notifyAtRepeat.getTime() - defaultValues.startAt.getTime()
        : 'null';

    reset({
      ...superDefaultValues,
      ...defaultValues,
      notifyAtNum: notifyNum,
      notifyAtRepeatNum: notifyRepeatNum,
      notifyAt: defaultValues.notifyAt ?? undefined,
      notifyAtRepeat: defaultValues.notifyAtRepeat ?? undefined,
    });

    notifyAtValue.current = notifyNum;
    notifyAtRepeatValue.current = notifyRepeatNum;

    setTimeout(() => setIsLoading(false), 100);
  }, [defaultValues, editId, reset]);

  const calcTextAreaHeight = useDebounceCallback(() => {
    const minHeight = 76;
    const val = getValues('description');
    const div = document.createElement('div');
    div.style.setProperty('padding', `${toRem(8)} ${toRem(12)}`);
    div.style.setProperty('max-width', toRem(376));
    div.innerHTML = val;
    document.body.append(div);

    let height = div.offsetHeight + 10;
    div.remove();
    if (height < minHeight) height = minHeight;

    setNoteHeight(toRem(height));
  }, 500);

  const onHivesChange = (newValues: IDropdownMenuItem[]) => {
    setValue(
      'hiveIds',
      newValues.map((el) => `${el.key}`)
    );
    setValue('hives', newValues);
  };

  const onDescriptionChange = (
    event: ChangeEvent<HTMLTextAreaElement>,
    field: ControllerRenderProps<NewEventFormValues, 'description'>
  ) => {
    field.onChange(event);
    calcTextAreaHeight();
  };

  const onStartDateChange = (activeDate?: Date) => {
    if (activeDate) {
      setValue('startAt', activeDate);
      setValue('startAtUtc', pickerDateToUtcIso(activeDate));
      setTimeout(() => {
        setEndAtMinDate(activeDate.getTime());

        if (getValues('endAt') < activeDate) {
          setValue('endAt', new Date(activeDate.getTime() + 900000));
          setValue('endAtUtc', pickerDateToUtcIso(new Date(activeDate.getTime() + 900000)));
        }
      }, 500);

      onNotifyAtChange(notifyAtValue.current);
      onnotifyAtRepeatChange(notifyAtRepeatValue.current);
    }
  };

  const onEndDateChange = (activeDate?: Date) => {
    if (activeDate) {
      const startAt = getValues('startAt');
      if (startAt > activeDate) {
        setValue('endAt', new Date(startAt.getTime() + 900000));
        setValue('endAtUtc', pickerDateToUtcIso(new Date(startAt.getTime() + 900000)));
      } else if (startAt.getTime() === activeDate.getTime()) {
        setValue('endAt', new Date(startAt.getTime() + 900000));
        setValue('endAtUtc', pickerDateToUtcIso(new Date(startAt.getTime() + 900000)));
      } else {
        setValue('endAt', activeDate);
        setValue('endAtUtc', pickerDateToUtcIso(activeDate));
      }
    }
  };

  const onAllDayChange = (value: boolean) => {
    if (value) {
      tempEndAt.current = getValues('endAt');
      const endOfDay = new Date(new Date(getValues('startAt')).setHours(23, 59, 59, 59));
      setValue('endAt', endOfDay);
      setValue('endAtUtc', pickerDateToUtcIso(endOfDay));
      setIsEndReadOnly(true);
    } else {
      setValue('endAt', tempEndAt.current || getValues('startAt'));
      setValue('endAtUtc', pickerDateToUtcIso(tempEndAt.current || getValues('startAt')));
      setIsEndReadOnly(false);
    }
  };

  const checkNotifies = () => {
    const notifyAt = getValues('notifyAt');
    const notifyAtRepeat = getValues('notifyAtRepeat');

    const notifyAtNum = getValues('notifyAtNum') !== 'null' ? Number(getValues('notifyAtNum')) : 0;
    const notifyAtRepeatNum =
      getValues('notifyAtRepeatNum') !== 'null' ? Number(getValues('notifyAtRepeatNum')) : 0;

    if (!notifyAtNum && !notifyAtRepeatNum) return;

    const arr = [
      {date: notifyAt, num: notifyAtNum},
      {date: notifyAtRepeat, num: notifyAtRepeatNum},
    ].sort((a, b) => Math.abs(b.num) - Math.abs(a.num));

    setValue('notifyAt', arr[0].date);
    setValue('notifyAtNum', arr[0].num);
    setValue('notifyAtRepeat', arr[1].date);
    setValue('notifyAtRepeatNum', arr[1].num);
  };

  const onNotifyAtChange = useDebounceCallback((value: string | number) => {
    notifyAtValue.current = value;
    setValue('notifyAtNum', value);

    const notifyAtDate =
      value !== 'null' ? new Date(getValues('startAt').getTime() + Number(value)) : undefined;
    setValue('notifyAt', notifyAtDate);

    checkNotifies();
  }, 300);

  const onnotifyAtRepeatChange = useDebounceCallback((value: string | number) => {
    notifyAtRepeatValue.current = value;
    setValue('notifyAtRepeatNum', value);

    const notifyAtRepeatDate =
      value !== 'null' ? new Date(getValues('startAt').getTime() + Number(value)) : undefined;
    setValue('notifyAtRepeat', notifyAtRepeatDate);

    checkNotifies();
  }, 300);

  const patchEvent = async (data: NewEventFormValues) => {
    if (!editId || !apiaryId) return;
    setIsLoading(true);
    console.log(data);

    let endAtDate = data.endAt;
    if (data.startAt.getTime() === data.endAt.getTime()) {
      endAtDate = new Date(data.startAt.getTime() + 900000);
      setValue('endAt', endAtDate);
      setValue('endAtUtc', pickerDateToUtcIso(endAtDate));
    }

    const payload: FullCalendarPatchRequestParams = {
      id: `${editId}`,
      startAt: data.startAtUtc || pickerDateToUtcIso(data.startAt),
      endAt: pickerDateToUtcIso(endAtDate),
      description: data.description,
      notifyAt: data.notifyAt ? pickerDateToUtcIso(data.notifyAt) : null,
      notifyAtRepeat: data.notifyAtRepeat ? pickerDateToUtcIso(data.notifyAtRepeat) : null,
      hiveIds: data.hiveIds,
    };

    try {
      const res = await editEvent(payload);
      if (res.status === 200 || res.status === 201) {
        toast.success('Событие успешно изменено');
        await getEventsFromServer({apiaryId});
        reset();
        onClose?.();
      } else {
        toast.error('Произошла ошибка, попробуйте позже, пожалуйста');
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || '';
      if (errorMessage.includes('endAt must be after startAt')) {
        const correctedEndAt = new Date(data.startAt.getTime() + 900000);
        setValue('endAt', correctedEndAt);
        setValue('endAtUtc', pickerDateToUtcIso(correctedEndAt));

        const correctedPayload: FullCalendarPatchRequestParams = {
          ...payload,
          endAt: pickerDateToUtcIso(correctedEndAt),
        };

        try {
          const retryRes = await editEvent(correctedPayload);
          if (retryRes.status === 200 || retryRes.status === 201) {
            toast.success('Событие успешно изменено');
            await getEventsFromServer({apiaryId});
            reset();
            onClose?.();
          } else {
            toast.error('Произошла ошибка, попробуйте позже, пожалуйста');
          }
        } catch (retryErr) {
          console.error(retryErr);
          toast.error('Произошла ошибка, попробуйте позже, пожалуйста');
        }
      } else {
        const errText = errorMessage || 'Произошла ошибка, попробуйте позже, пожалуйста';
        toast.error(errText);
      }
    }

    setIsLoading(false);
    setIsEndReadOnly(false);
  };

  const createEvent = async (data: NewEventFormValues) => {
    if (!apiaryId) return;
    setIsLoading(true);

    let endAtDate = data.endAt;
    if (data.startAt.getTime() === data.endAt.getTime()) {
      endAtDate = new Date(data.startAt.getTime() + 900000);
      setValue('endAt', endAtDate);
      setValue('endAtUtc', pickerDateToUtcIso(endAtDate));
    }

    const hiveIds = data.hiveIds.length ? data.hiveIds : hives.map((el) => `${el.id}`);

    const payload: CalendarPostRequestParams = {
      apiaryId,
      hiveIds: hiveIds,
      startAt: data.startAtUtc || pickerDateToUtcIso(data.startAt),
      endAt: pickerDateToUtcIso(endAtDate),
      description: data.description,
      notifyAt: data.notifyAt ? pickerDateToUtcIso(data.notifyAt) : null,
      notifyAtRepeat: data.notifyAtRepeat ? pickerDateToUtcIso(data.notifyAtRepeat) : null,
    };

    try {
      const res = await addEvent(payload);
      if (res?.status === 200 || res?.status === 201) {
        toast.success('Событие успешно создано');
        await getEventsFromServer({apiaryId});
        onClose?.();
        reset();
      } else {
        toast.error('Произошла ошибка, попробуйте позже, пожалуйста');
        onClose?.();
        reset();
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || '';
      if (errorMessage.includes('endAt must be after startAt')) {
        const correctedEndAt = new Date(data.startAt.getTime() + 900000);
        setValue('endAt', correctedEndAt);
        setValue('endAtUtc', pickerDateToUtcIso(correctedEndAt));

        const correctedPayload: CalendarPostRequestParams = {
          ...payload,
          endAt: pickerDateToUtcIso(correctedEndAt),
        };

        try {
          const retryRes = await addEvent(correctedPayload);
          if (retryRes?.status === 200 || retryRes?.status === 201) {
            toast.success('Событие успешно создано');
            await getEventsFromServer({apiaryId});
            onClose?.();
            reset();
          } else {
            toast.error('Произошла ошибка, попробуйте позже, пожалуйста');
            onClose?.();
            reset();
          }
        } catch (retryErr) {
          console.error(retryErr);
          toast.error('Произошла ошибка, попробуйте позже, пожалуйста');
          onClose?.();
          reset();
        }
      } else {
        const errText = errorMessage || 'Произошла ошибка, попробуйте позже, пожалуйста';
        toast.error(errText);
        onClose?.();
        reset();
      }
    }

    setTimeout(() => {
      setIsLoading(false);
      setIsEndReadOnly(false);
    }, 300);
  };

  const onSubmit = (data: NewEventFormValues) => {
    console.log(data);
    if (editId) {
      return patchEvent(data);
    }

    createEvent(data);
  };

  const onSubmitDebounced = useDebounceCallback(onSubmit, 100);

  useEffect(() => {
    if (editId && defaultValues) {
      if (defaultValues?.notifyAt && defaultValues?.startAt) {
        const diff = defaultValues.notifyAt.getTime() - defaultValues.startAt.getTime();
        setNotifyAtDefaultValue(diff);
      }
      if (defaultValues?.notifyAtRepeat && defaultValues?.startAt) {
        const diff = defaultValues.notifyAtRepeat.getTime() - defaultValues.startAt.getTime();
        setNotifyAtRepeatDefaultValue(diff);
      }

      setTimeout(() => {
        // if (defaultValues?.hives?.length) {
        //   onHivesChange(defaultValues.hives.filter(el => el&&el.key&&el.value))
        // }

        setIsLoading(false);
      }, 200);
    }
  }, [defaultValues, editId]);

  return {
    isLoading,
    hives,
    control,
    noteHeight,
    notifySelectItems,
    isEndReadOnly,
    endAtMinDate,
    notifyAtDefaultValue,
    notifyAtRepeatDefaultValue,
    errors,
    onHivesChange,
    onDescriptionChange,
    handleSubmit,
    onSubmit,
    onStartDateChange,
    onEndDateChange,
    onAllDayChange,
    onNotifyAtChange,
    onnotifyAtRepeatChange,
    reset,
    onSubmitDebounced,
  };
};
